import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@jobenvoy/ui";
import { describeError } from "@/lib/api/client";
import { deleteTailored, getTailoredPdf, listTailored } from "@/lib/api/resumeService";
import { TAILOR_HISTORY_PAGE } from "@/lib/tailorHistory";
import { keys } from "./keys";
import { useSingleFlightMutation } from "./singleFlight";

/** A page of the person's tailored resumes, the newest first. */
export function useTailorHistory(cursor: string | undefined) {
    return useQuery({
        queryKey: [...keys.resume.all, "tailored-list", cursor ?? null] as const,
        queryFn: () => listTailored(cursor, TAILOR_HISTORY_PAGE),
        placeholderData: keepPreviousData,
        staleTime: 30_000,
    });
}

/**
 * One tailored resume's PDF, read through the API. A tailored resume
 * never changes, so a read is kept for as long as it is shown, and the
 * thumbnail and the viewer share it.
 */
export function useTailoredPdf(resumeId: string | null) {
    return useQuery({
        queryKey: [...keys.resume.all, "tailored-pdf", resumeId] as const,
        queryFn: () => getTailoredPdf(resumeId as string),
        enabled: resumeId !== null,
        staleTime: Infinity,
        gcTime: 5 * 60_000,
    });
}

/** Deletes a tailored resume, then reads the list again. Says so either way. */
export function useDeleteTailored() {
    const client = useQueryClient();
    const toast = useToast();
    return useSingleFlightMutation({
        mutationFn: (resumeId: string) => deleteTailored(resumeId),
        onSuccess: () => {
            toast.success("Resume deleted");
            return client.invalidateQueries({ queryKey: keys.resume.all });
        },
        onError: (error) => toast.error("Not deleted", describeError(error)),
    });
}
